import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@klareospace/pharos-lib-web-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';
import type { Control } from 'react-hook-form';

import { useVehicles } from '../../vehicles/hooks';
import { buildVehicleQueryFilter } from './driver-vehicle-field.utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriverVehicleFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  filteredVehicleOptions: MultiSelectOption[];
  setVehiclePopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setVehicleSearch: React.Dispatch<React.SetStateAction<string>>;
  vehicleOptions: MultiSelectOption[];
  vehiclePopoverOpen: boolean;
  vehicleSearch: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DriverVehicleField({
  control,
  filteredVehicleOptions,
  setVehiclePopoverOpen,
  setVehicleSearch,
  vehicleOptions,
  vehiclePopoverOpen,
  vehicleSearch,
}: DriverVehicleFieldProps): React.JSX.Element {
  const { t } = useTranslation('settings');

  return (
    <FormField
      control={control}
      name="defaultVehicleId"
      render={({ field }): React.JSX.Element => {
        const selectedLabel = field.value
          ? (vehicleOptions.find((o) => o.value === field.value)?.label ?? field.value)
          : null;

        function handleVehicleSelect(value: string): void {
          field.onChange(value === '__none__' ? null : value);
          setVehiclePopoverOpen(false);
          setVehicleSearch('');
        }

        return (
          <FormItem>
            <FormLabel>{t('drivers.form.defaultVehicle')}</FormLabel>
            <Popover onOpenChange={setVehiclePopoverOpen} open={vehiclePopoverOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    className="w-full justify-between font-normal"
                    role="combobox"
                    type="button"
                    variant="outline"
                  >
                    <span className={cn(!selectedLabel && 'text-muted-foreground')}>
                      {selectedLabel ?? t('drivers.form.defaultVehiclePlaceholder')}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
                <Command shouldFilter={false}>
                  <CommandInput
                    onValueChange={setVehicleSearch}
                    placeholder={t('drivers.form.vehicleSearch')}
                    value={vehicleSearch}
                  />
                  <CommandList>
                    <CommandEmpty>{t('drivers.form.defaultVehiclePlaceholder')}</CommandEmpty>
                    <CommandGroup>
                      <CommandItem onSelect={(): void => handleVehicleSelect('__none__')} value="__none__">
                        <Check className={cn('mr-2 h-4 w-4', !field.value ? 'opacity-100' : 'opacity-0')} />
                        {t('drivers.form.defaultVehiclePlaceholder')}
                      </CommandItem>
                      {filteredVehicleOptions.map((opt) => (
                        <CommandItem
                          key={opt.value}
                          onSelect={(): void => handleVehicleSelect(opt.value)}
                          value={opt.value}
                        >
                          <Check
                            className={cn('mr-2 h-4 w-4', field.value === opt.value ? 'opacity-100' : 'opacity-0')}
                          />
                          {opt.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseDriverVehicleFieldReturn {
  filteredVehicleOptions: MultiSelectOption[];
  setVehiclePopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setVehicleSearch: React.Dispatch<React.SetStateAction<string>>;
  vehicleOptions: MultiSelectOption[];
  vehiclePopoverOpen: boolean;
  vehicleSearch: string;
  /** True once the vehicles query has data for the current carrier/site filter. */
  vehiclesLoaded: boolean;
}

export function useDriverVehicleField(
  open: boolean,
  carrierId: string | undefined,
  hasPermission: (p: string) => boolean,
  driverId?: string,
  siteIds?: string[],
  assignedVehicleId?: string | null,
  spaceId?: string | null,
): UseDriverVehicleFieldReturn {
  const { data: vehiclesData } = useVehicles(
    buildFilteredVehiclesQuery(open, carrierId, hasPermission, driverId, siteIds, spaceId),
  );

  // The driver's persisted assignment may not match the site filter (pre-existing data drift).
  // Fetch it explicitly so it stays visible and selected — mirrors the carrier dropdown's
  // assigned-carrier escape hatch. Only honored while the form's carrier still matches the
  // vehicle's carrier: changing the carrier must drop (and clear) the old assignment.
  const { data: assignedVehicleData } = useVehicles(buildAssignedVehicleQuery(open, assignedVehicleId, hasPermission));

  const vehicleOptions = useMemo<MultiSelectOption[]>(
    () => buildVehicleOptions(vehiclesData?.items, assignedVehicleData?.items[0], carrierId),
    [vehiclesData, assignedVehicleData, carrierId],
  );

  const [vehiclePopoverOpen, setVehiclePopoverOpen] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');

  const filteredVehicleOptions = useMemo<MultiSelectOption[]>(() => {
    const query = vehicleSearch.toLowerCase();
    if (!query) return vehicleOptions;
    return vehicleOptions.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [vehicleOptions, vehicleSearch]);

  useEffect((): void => {
    if (!open) {
      setVehicleSearch('');
      setVehiclePopoverOpen(false);
    }
  }, [open]);

  return {
    filteredVehicleOptions,
    setVehiclePopoverOpen,
    setVehicleSearch,
    vehicleOptions,
    vehiclePopoverOpen,
    vehicleSearch,
    // The prune-on-filter-change effect must not run before the escape-hatch query settles,
    // or it would wrongly clear the driver's persisted assignment while it is still loading.
    vehiclesLoaded: vehiclesData !== undefined && (!assignedVehicleId || assignedVehicleData !== undefined),
  };
}

// Vehicles matching the dropdown filter: always scoped to the driver's space (so a national /
// multi-space carrier cannot surface vehicles from other spaces, #6) and to the driver's site
// context; the carrier filter applies only once a carrier is selected, so site-filtered vehicles
// appear immediately (decision A).
function buildFilteredVehiclesQuery(
  open: boolean,
  carrierId: string | undefined,
  hasPermission: (p: string) => boolean,
  driverId: string | undefined,
  siteIds: string[] | undefined,
  spaceId: string | null | undefined,
): Parameters<typeof useVehicles>[0] {
  return buildVehicleQueryFilter({
    canList: hasPermission('vehicles:list'),
    carrierId,
    driverId,
    open,
    siteIds: siteIds ?? [],
    spaceId: spaceId ?? null,
  });
}

// The driver's currently-assigned vehicle, fetched by id regardless of the dropdown filter.
function buildAssignedVehicleQuery(
  open: boolean,
  assignedVehicleId: string | null | undefined,
  hasPermission: (p: string) => boolean,
): Parameters<typeof useVehicles>[0] {
  return {
    enabled: open && !!assignedVehicleId && hasPermission('vehicles:list'),
    ids: assignedVehicleId ? [assignedVehicleId] : undefined,
    page: 1,
    pageSize: 1,
  };
}

// Maps the filtered vehicles to options and appends the driver's persisted assignment when it
// would otherwise be filtered out (site drift) — only while its carrier matches the selection.
function buildVehicleOptions(
  vehicles: { id: string; licensePlate: string }[] | undefined,
  assigned: { carrierId: string; id: string; licensePlate: string } | undefined,
  carrierId: string | undefined,
): MultiSelectOption[] {
  const options = (vehicles ?? []).map((v) => ({ label: v.licensePlate, value: v.id }));
  if (assigned && assigned.carrierId === carrierId && !options.some((o) => o.value === assigned.id)) {
    options.push({ label: assigned.licensePlate, value: assigned.id });
  }
  return options;
}
